import { FC } from "react";
import cx from "classnames";
import { PropertyVisualStyle, ShowIfNotSetValues } from "../../../types/site";
import { Vehicle } from "@/types/vehicle";
import InfoIcon from "@/ui/icons/info-icon";
import styles from "./pricePropertyValue.module.scss";
import CustomTooltip, { CustomTooltipType } from "./customTooltip/customTooltip";
import { Translations } from "../../../utils/translations";
import CallToPrice from "./call-to-price";
import LockedIcon from "../../icons/locked-icon";

interface IPricePropertyValue {
  propertyValue?: string;
  className: string;
  vehicle?: Vehicle;
  propertyVisualStyle: PropertyVisualStyle;
  disclaimer?: string;
  tooltipType?: CustomTooltipType;
  showIfNotSet: ShowIfNotSetValues;
  callToPriceClassName: string;
  phone: string;
  translations: Translations;
  lockedText?: string;
  lockTextLeftGradientColor?: string;
  lockTextRightGradientColor?: string;
}

function getStyleObject(propertyVisualStyle: PropertyVisualStyle, vehicle?: Vehicle): any {
  return {
    [styles.inverted]: propertyVisualStyle === PropertyVisualStyle.INVERTED,
    [styles.strikeout]:
      propertyVisualStyle === PropertyVisualStyle.STRIKEOUT_PRICE ||
      (propertyVisualStyle === PropertyVisualStyle.PRELIMINARY_PRICE && vehicle?.finalPrice !== undefined),
    [styles.gray]: propertyVisualStyle === PropertyVisualStyle.PRELIMINARY_PRICE && vehicle?.finalPrice !== undefined,
    [styles.finalPrice]: propertyVisualStyle === PropertyVisualStyle.FINAL_PRICE,
    [styles.discount]: propertyVisualStyle === PropertyVisualStyle.DISCOUNT,
    [styles.preliminaryValue]: propertyVisualStyle === PropertyVisualStyle.PRELIMINARY_PRICE,
  };
}

const PricePropertyValue: FC<IPricePropertyValue> = ({
  propertyValue,
  className,
  vehicle,
  propertyVisualStyle,
  disclaimer,
  tooltipType,
  showIfNotSet,
  callToPriceClassName,
  phone,
  translations,
  lockedText,
  lockTextLeftGradientColor,
  lockTextRightGradientColor,
}) => {
  let value = propertyValue;
  if (!value) {
    if (showIfNotSet === ShowIfNotSetValues.CALL_TO_PRICE) {
      return <CallToPrice phone={phone} translations={translations} className={callToPriceClassName} />;
    }
    if (showIfNotSet === ShowIfNotSetValues.LOCKED) {
      return (
        <div
          className={styles.locked}
          style={
            lockTextLeftGradientColor && lockTextRightGradientColor
              ? {
                  background: `linear-gradient(90deg, ${lockTextLeftGradientColor} 0%, ${lockTextRightGradientColor} 100%)`,
                }
              : {}
          }
        >
          <LockedIcon />
          {lockedText}
        </div>
      );
    }

    if (showIfNotSet === ShowIfNotSetValues.NOTHING) {
      return null;
    }
  }

  if (value) {
    if (value.charAt(0) === "-") {
      value = "-$" + value.substring(1);
    } else {
      value = "$" + value;
    }
  }

  return disclaimer ? (
    <CustomTooltip
      target={
        <span className={cx(className, getStyleObject(propertyVisualStyle, vehicle))}>
          <span>{value}</span>{" "}
          <div
            className={cx(styles.infoIcon, {
              [styles.big]: tooltipType === CustomTooltipType.SRP_GRID_PRICE,
            })}
          >
            <InfoIcon />
          </div>
        </span>
      }
      title={disclaimer}
      type={tooltipType}
    />
  ) : (
    <p className={cx(className, styles.price, getStyleObject(propertyVisualStyle, vehicle))}>{value}</p>
  );
};

export default PricePropertyValue;
